import { customElement, property, query, state } from 'lit/decorators.js';
import { css, html, LitElement, nothing } from 'lit';
import Debug from 'debug';
import { TreeNode, Tree } from '../../flow/tree/common';
import { PdqTree } from '../../flow/pdq-tree';
import { PdqTreeView } from './pdq-tree-view';

const log = Debug('pdq:ui:pdq-tree-view-item');
@customElement('pdq-tree-view-item')
export class PdqTreeViewItem extends LitElement {
  static styles = css`
    * {
        box-sizing: border-box;
        display: block;
    }  
    ul, li {
        list-style-type: none;
        margin: 0;
        padding: 0;
    }
    .item {
        cursor: pointer;
        padding: 4px;
        border-left: 2px solid transparent;
        background-color: var(--cds-layer-01, #f4f4f4);
        color: var(--cds-text-secondary, #525252);

        -webkit-user-select: none;
        -moz-user-select: none;
        -ms-user-select: none;
        user-select: none;
        
        position: relative;
    }
    .hover {
        background-color: var(--cds-layer-hover-01, #e8e8e8);
    }
    .selected {
        border-left: 2px solid var(--cds-primary, #3d70b2);
        background-color: var(--cds-layer-selected-01, #e0e0e0);
    }
    :active {
        background-color: var(--cds-focus);
    }
    .expando {
        transition: transform 0.3s ease;
    }
    .expando.collapsed {
        transform: rotate(-90deg);
    }
  `;

  @property({ type: Object }) node: TreeNode | undefined;
  @property({ type: Object }) tree: PdqTreeView | undefined;
  @property({ type: Number }) depth = 0;

  @state() selected = false;
  @state() expanded = true;

  @query('.item') itemElement: HTMLElement | undefined;


  firstUpdated() {
    this.tree?.addEventListener('selection-changed', this.boundSelectionChange);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.tree?.removeEventListener('selection-changed', this.boundSelectionChange);
  }

  render() {
    //log(`render`, this.depth)

    if (!this.node || !this.tree)
      return nothing;

    return html`
      <li role="treeitem">
        
        <div 
            class="item ${this.node.selected ? 'selected' : 'not-selected'}"
            @mouseover=${this.mouseover}
            @mouseout=${this.mouseout}
            @click=${this.select} 
            @keydown=${this.keydown} 
            tabindex="0"
            style="padding-left: ${2 + 16 + (Number(this.depth) * 8)}px"
          >
          ${this.renderExpando()}
          ${this.node.title}
        </div>
        
        ${this.renderItems()}
      </li>
    `;
  }

  renderExpando() {
    const hasItems = (this.node?.items || [])?.length > 0;
    if (!hasItems)
      return nothing;


    return html`<pdq-icon
        style="position: absolute; left: ${2 + (Number(this.depth) * 8)}px"
        class="expando ${this.node!.expanded ? 'expanded' : 'collapsed'}"
        icon="caretDown"
        role="button" 
        aria-expanded=${this.expanded} 
        aria-label="Collapse" 
        @click=${this.boundToggleExpand}></pdq-icon>`;
  }

  renderItems() {
    if (!this.node)
      return

    if (!this.expanded)
      return nothing;

    const hasItems = (this.node.items || []).length > 0;
    if (!hasItems)
      return nothing;

    return html`
      <ul role="group">
        ${this.node.items
        .map((i: TreeNode) => html`<pdq-tree-view-item 
            .node=${i}
            .tree=${this.tree}
            .depth=${this.depth + 1}
          ></pdq-tree-view-item>`)}
      </ul>`;
  }

  toggleExpand(evt: Event) {
    evt.stopImmediatePropagation()
    this.tree.tree.toggleExpand(this.node);
    this.expanded = !this.expanded;
    this.requestUpdate('expanded', !this.expanded);
    this.tree?.dispatchEvent(new CustomEvent('expand-changed', {
      detail: {
        id: this.node?.id,
        hierarchy: this.node?.hierarchy,
        expanded: this.expanded
      }, bubbles: true}));
    log(`toggleExpand`, this.node?.id, this.expanded);
  }

  select(evt: CustomEvent) {
    log('select');
    if (this.node) {
      evt.stopImmediatePropagation();
      this.selected = !this.selected;
      this.tree?.select(this.node.id);
      this.requestUpdate('selected', !this.selected);
    }
  }

  selectionChange(evt: CustomEvent) {
    //log('selectionChange', evt.detail);
    //todo - review ... is this still needed?
    if (!this.node)
      return;

    if (this.selected !== this.node.selected) {
      this.selected = this.node.selected;
      this.requestUpdate('selected', !this.selected);
    }
    //
    // if (evt.detail.id == this.node.id) {
    //   if (this.selected != evt.detail.selected) {
    //     this.selected = evt.detail.selected;
    //     this.requestUpdate('selected', !this.selected);
    //   }
    //   return;
    // }
    // if (!evt.detail.multiple && evt.detail.selected && this.selected) {
    //   this.selected = false;
    //   this.requestUpdate('selected', !this.selected);
    // }
  }

  mouseover(evt: Event): void {
    evt.stopImmediatePropagation();
    this.itemElement?.classList.add('hover');
    //log('mouseover', this.node)
  }

  mouseout(evt: Event): void {
    evt.stopImmediatePropagation();
    this.itemElement?.classList.remove('hover');
    //log('mouseout', this.node)
  }

  keydown(evt: KeyboardEvent): void {
    if (evt.code === 'Enter' || evt.code === 'Space') {
      this.select(evt as any as Event);
    }

    if (evt.code === 'ArrowRight' && !this.expanded) {
      this.toggleExpand(evt);
    }
    if (evt.code === 'ArrowLeft' && this.expanded) {
      this.toggleExpand(evt);
    }
  }

  boundSelectionChange = this.selectionChange.bind(this);
  boundToggleExpand = this.toggleExpand.bind(this);
}